import React, { useState, useEffect, useMemo } from 'react';

const PAGE_SIZE = 10;

const DriverSalaryLogs = ({ user, operatorDetails }) => {
    const [logs, setLogs] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [page, setPage] = useState(1);
    const [toast, setToast] = useState(null);
    
    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const [newLog, setNewLog] = useState({
        driverId: '',
        month: new Date().toISOString().slice(0, 7),
        baseSalary: 0,
        fixedAllowances: 0,
        overtimeAmount: 0,
        uniformDeduction: 0,
        accommodationDeduction: 0,
        otherDeduction: 0,
        status: 'Paid',
        remarks: ''
    });

    useEffect(() => {
        fetchDrivers();
        fetchLogs();
    }, [selectedMonth, user]);

    const fetchDrivers = async () => {
        try {
            const res = await fetch(`/api/drivers/${user.operatorId}`);
            const data = await res.json();
            // Filter only direct drivers for salary logs
            setDrivers(Array.isArray(data) ? data.filter(d => !d.vendorId) : []);
        } catch (err) {
            console.error("Error fetching drivers:", err);
        }
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/driver-salary-logs/${user.operatorId}?month=${selectedMonth}`);
            const data = await res.json();
            setLogs(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching logs:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDriverChange = (driverId) => {
        const driver = drivers.find(d => d._id === driverId);
        if (driver) {
            setNewLog({
                ...newLog,
                driverId,
                baseSalary: driver.baseSalary || 0,
                fixedAllowances: driver.fixedAllowances || 0,
                uniformDeduction: driver.uniformCost || 0,
                accommodationDeduction: driver.accommodationCost || 0,
                otherDeduction: driver.otherDeduction || 0,
            });
        } else {
            setNewLog({ ...newLog, driverId: '' });
        }
    };

    const calculateTotal = (log) => {
        return (parseFloat(log.baseSalary) || 0) +
               (parseFloat(log.fixedAllowances) || 0) +
               (parseFloat(log.overtimeAmount) || 0) -
               (parseFloat(log.uniformDeduction) || 0) -
               (parseFloat(log.accommodationDeduction) || 0) -
               (parseFloat(log.otherDeduction) || 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const totalPayout = calculateTotal(newLog);
        try {
            const payload = { 
                ...newLog, 
                operatorId: user.operatorId, 
                totalPayout,
                paidAt: newLog.status === 'Paid' ? new Date() : null
            };
            const res = await fetch('/api/driver-salary-logs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const text = await res.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch (e) {
                showToast("Server returned invalid response", "error");
                return;
            }

            if (res.ok) {
                setShowCreateModal(false);
                fetchLogs();
                showToast("Salary log created successfully");
            } else {
                showToast(data.message || "Error creating log", "error");
            }
        } catch (err) {
            showToast("Network Error: " + err.message, "error");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this log?")) return;
        try {
            const res = await fetch(`/api/driver-salary-logs/${id}`, { method: 'DELETE' });
            if (res.ok) {
                showToast("Log deleted successfully");
                fetchLogs();
            } else {
                showToast("Failed to delete log", "error");
            }
        } catch (err) {
            showToast("Server error", "error");
        }
    };

    const handleStatusUpdate = async (log, status) => {
        try {
            const res = await fetch(`/api/driver-salary-logs/${log._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status, paidAt: status === 'Paid' ? new Date() : null })
            });
            if (res.ok) {
                showToast(`Status updated to ${status}`);
                fetchLogs();
            } else {
                showToast("Failed to update status", "error");
            }
        } catch (err) {
            showToast("Server error", "error");
        }
    };

    const stats = useMemo(() => {
        const totalPayout = logs.reduce((sum, log) => sum + (log.totalPayout || 0), 0);
        const paidCount = logs.filter(l => l.status === 'Paid').length;
        const pendingCount = logs.filter(l => l.status === 'Pending').length;
        return { totalPayout, paidCount, pendingCount, totalEntries: logs.length };
    }, [logs]);

    const totalPages = Math.ceil(logs.length / PAGE_SIZE);
    const paginatedLogs = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
        </svg>
    );

    const icons = {
        plus: 'M12 5v14 M5 12h14',
        search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
        dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
        check: 'M20 6L9 17l-5-5',
        clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    };

    return (
        <div className="manage-drivers-page">
            <style>{`
                .manage-drivers-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
                .title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .subtitle { color: #64748b; font-size: 14px; margin-top: 4px; }
                
                .premium-btn {
                    display: flex; align-items: center; gap: 10px; padding: 12px 24px; border-radius: 14px;
                    font-weight: 700; font-size: 14px; cursor: pointer; transition: all 0.3s;
                    border: none; color: white; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }
                .btn-primary { background: #4338ca; }
                
                .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-bottom: 24px; }
                .stat-card {
                    background: white; border-radius: 16px; padding: 20px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 16px; transition: all 0.25s;
                }
                .stat-icon-wrap {
                    width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
                }
                .stat-label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 2px; }
                .stat-val { font-size: 20px; font-weight: 800; color: #1e293b; }

                .toolbar { 
                    display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; 
                    background: white; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0;
                }
                .month-picker { padding: 10px; border-radius: 10px; border: 1px solid #e2e8f0; font-family: inherit; font-weight: 600; }

                .table-container { 
                    background: white; border-radius: 16px; overflow: hidden; border: 1px solid #e2e8f0;
                }
                .premium-table { width: 100%; border-collapse: collapse; }
                .premium-table th { 
                    background: #f8fafc; padding: 16px 20px; text-align: left; font-size: 12px; 
                    font-weight: 600; color: #64748b; text-transform: uppercase; border-bottom: 1px solid #e2e8f0;
                }
                .premium-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #334155; }
                
                .status-badge { padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
                .status-paid { background: #ecfdf5; color: #059669; }
                .status-pending { background: #fffbeb; color: #d97706; }

                .op-modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.5); backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .op-modal {
                    background: white; border-radius: 20px; width: 100%; max-width: 600px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); overflow: hidden;
                }
                .modal-header { padding: 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .modal-body { padding: 24px; }
                .modal-footer { padding: 20px 24px; background: #f8fafc; display: flex; justify-content: flex-end; gap: 12px; }
                
                .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                .form-group { display: flex; flex-direction: column; gap: 6px; }
                .form-group label { font-size: 12px; font-weight: 600; color: #64748b; }
                .form-input { padding: 10px; border: 1px solid #e2e8f0; border-radius: 8px; outline: none; }
                .form-input:focus { border-color: #6366f1; }
                
                .payout-box { 
                    grid-column: span 2; background: #f1f5f9; padding: 16px; border-radius: 12px;
                    display: flex; justify-content: space-between; align-items: center; margin-top: 10px;
                }
                .toast {
                    position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 10px;
                    background: #1e293b; color: white; z-index: 2000; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    animation: slideIn 0.3s ease-out; font-weight: 600;
                }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }
                @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header">
                <div>
                    <h1 className="title">Driver Salary Logs</h1>
                    <p className="subtitle">Track monthly payouts, allowances, and deductions for your fleet</p>
                </div>
                <button className="premium-btn btn-primary" onClick={() => setShowCreateModal(true)}>
                    <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                    Generate Salary Entry
                </button>
            </div>

            <div className="stats-grid">
                <div className="stat-card" style={{ borderLeft: '4px solid #4338ca' }}>
                    <div className="stat-icon-wrap" style={{ background: '#eef2ff' }}>
                        <Icon d={icons.dollar} color="#4338ca" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Total Expenditure ({selectedMonth})</span>
                        <span className="stat-val">₹{stats.totalPayout.toLocaleString()}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
                    <div className="stat-icon-wrap" style={{ background: '#ecfdf5' }}>
                        <Icon d={icons.check} color="#059669" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Paid Entries</span>
                        <span className="stat-val">{stats.paidCount}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                    <div className="stat-icon-wrap" style={{ background: '#fffbeb' }}>
                        <Icon d={icons.clock} color="#d97706" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Pending Payouts</span>
                        <span className="stat-val">{stats.pendingCount}</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrap" style={{ background: '#f8fafc' }}>
                        <Icon d={icons.search} color="#64748b" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Total Records</span>
                        <span className="stat-val">{stats.totalEntries}</span>
                    </div>
                </div>
            </div>

            <div className="toolbar">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#64748b' }}>Select Period:</span>
                    <input 
                        type="month" 
                        className="month-picker"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    />
                </div>
            </div>

            <div className="table-container">
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Driver</th>
                            <th>Month</th>
                            <th>Base Salary</th>
                            <th>Allowances</th>
                            <th>Overtime</th>
                            <th>Deductions</th>
                            <th>Total Payout</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="9" style={{ textAlign: 'center', padding: 60 }}>Loading records...</td></tr>
                        ) : paginatedLogs.length === 0 ? (
                            <tr><td colSpan="9" style={{ textAlign: 'center', padding: 60 }}>No salary logs found for this period.</td></tr>
                        ) : paginatedLogs.map(log => {
                            const deductions = (log.uniformDeduction || 0) + (log.accommodationDeduction || 0) + (log.otherDeduction || 0);
                            return (
                                <tr key={log._id}>
                                    <td>
                                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.driverId?.name}</div>
                                        <div style={{ fontSize: 12, color: '#64748b' }}>{log.driverId?.driverCode || 'N/A'}</div>
                                    </td>
                                    <td>{log.month}</td>
                                    <td>₹{log.baseSalary?.toLocaleString()}</td>
                                    <td>₹{log.fixedAllowances?.toLocaleString()}</td>
                                    <td>₹{log.overtimeAmount?.toLocaleString()}</td>
                                    <td style={{ color: '#ef4444', fontWeight: 500 }}>-₹{deductions?.toLocaleString()}</td>
                                    <td style={{ fontWeight: 800, color: '#0f172a', fontSize: 15 }}>₹{log.totalPayout?.toLocaleString()}</td>
                                    <td>
                                        <button 
                                            className={`status-badge status-${log.status.toLowerCase()}`}
                                            style={{ border: 'none', cursor: 'pointer' }}
                                            onClick={() => handleStatusUpdate(log, log.status === 'Paid' ? 'Pending' : 'Paid')}
                                            title={`Click to mark as ${log.status === 'Paid' ? 'Pending' : 'Paid'}`}
                                        >
                                            {log.status}
                                        </button>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                            <button 
                                                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 4 }}
                                                onClick={() => handleDelete(log._id)}
                                                title="Delete Log"
                                            >
                                                <Icon d={icons.trash} size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreateModal && (
                <div className="op-modal-overlay">
                    <div className="op-modal">
                        <div className="modal-header">
                            <h3 style={{ margin: 0, fontWeight: 800 }}>Generate Salary Entry</h3>
                            <button style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowCreateModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-grid">
                                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                        <label>Select Driver</label>
                                        <select className="form-input" required value={newLog.driverId} onChange={(e) => handleDriverChange(e.target.value)}>
                                            <option value="">Choose a driver...</option>
                                            {drivers.map(d => (
                                                <option key={d._id} value={d._id}>{d.name} ({d.driverCode || 'N/A'})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Month</label>
                                        <input type="month" className="form-input" required value={newLog.month} onChange={(e) => setNewLog({ ...newLog, month: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Base Salary</label>
                                        <input type="number" className="form-input" value={newLog.baseSalary} onChange={(e) => setNewLog({ ...newLog, baseSalary: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Fixed Allowances</label>
                                        <input type="number" className="form-input" value={newLog.fixedAllowances} onChange={(e) => setNewLog({ ...newLog, fixedAllowances: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Overtime Amount</label>
                                        <input type="number" className="form-input" value={newLog.overtimeAmount} onChange={(e) => setNewLog({ ...newLog, overtimeAmount: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Uniform Deduction</label>
                                        <input type="number" className="form-input" value={newLog.uniformDeduction} onChange={(e) => setNewLog({ ...newLog, uniformDeduction: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Accommodation Cost</label>
                                        <input type="number" className="form-input" value={newLog.accommodationDeduction} onChange={(e) => setNewLog({ ...newLog, accommodationDeduction: e.target.value })} />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                        <label>Other Deductions</label>
                                        <input type="number" className="form-input" value={newLog.otherDeduction} onChange={(e) => setNewLog({ ...newLog, otherDeduction: e.target.value })} />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                        <label>Payment Status</label>
                                        <select className="form-input" value={newLog.status} onChange={(e) => setNewLog({ ...newLog, status: e.target.value })}>
                                            <option value="Paid">Paid (Default)</option>
                                            <option value="Pending">Pending</option>
                                        </select>
                                    </div>
                                    
                                    <div className="payout-box">
                                        <span style={{ fontWeight: 600, color: '#64748b' }}>Estimated Net Payout</span>
                                        <span style={{ fontSize: 22, fontWeight: 900, color: '#0f172a' }}>
                                            ₹{calculateTotal(newLog).toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="premium-btn" style={{ background: '#f1f5f9', color: '#64748b', boxShadow: 'none' }} onClick={() => setShowCreateModal(false)}>Cancel</button>
                                <button type="submit" className="premium-btn btn-primary">Save Salary Entry</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DriverSalaryLogs;
